/**
 * 10.6H — OBJETIVOS DA ÁREA (o que destranca o portão).
 *
 * Antes o portão de uma área abria só por derrotar o boss dela: dava para atravessar o jogo
 * inteiro ignorando 90% do mapa. Agora cada área tem uma LISTA FIXA de objetivos e o portão
 * fica TRANCADO até todos estarem cumpridos. Isso é diferente das três missões rotativas
 * (aquelas seguem existindo, dão Fragmentos e XP e nunca acabam); estes aqui são o contrato
 * de conclusão da área.
 *
 * Os alvos crescem com a PROFUNDIDADE: ao fechar a última área o mundo reinicia mais forte
 * (ver `Depth`), e os objetivos voltam com números maiores. É o que dá conteúdo sem fim.
 *
 * Estado puro (sem DOM, sem three.js): quem conta os eventos é o Game.
 */
import { GameConfig } from "../config/GameConfig";

export type AreaGoalKind = "territories" | "champions" | "kills" | "chests" | "boss";

export interface AreaGoal {
  kind: AreaGoalKind;
  progress: number;
  target: number;
}

export interface AreaGoalsSnapshot {
  /** regionId -> [progresso de cada objetivo, na ordem da config] */
  byRegion: Record<string, number[]>;
  /** regionId -> quantas vezes a área já foi concluída (sobe a cada Profundidade) */
  cleared: Record<string, number>;
  /** 10.9L6 — regionId -> territórios JÁ VISITADOS nesta volta ("explorar" conta cada um 1× só) */
  seen?: Record<string, string[]>;
}

interface GoalTemplate {
  kind: AreaGoalKind;
  target: number;
}

export class AreaGoals {
  private readonly progress: Record<string, number[]> = {};
  private readonly cleared: Record<string, number> = {};
  /**
   * 10.9L6 — BUG: "explorar territórios" somava +1 a CADA travessia de divisa (ir e voltar entre
   * dois territórios fechava a missão). Agora guarda quais territórios já contaram nesta volta.
   */
  private readonly seen: Record<string, Set<string>> = {};
  private legacySave = false;

  constructor(private readonly depth: () => number) {}

  private templates(regionId: string): GoalTemplate[] {
    const all = GameConfig.areaGoals as unknown as Record<string, readonly GoalTemplate[]>;
    return (all[regionId] ?? []) as GoalTemplate[];
  }

  /**
   * Alvo com Profundidade aplicada (o boss continua sendo 1 por volta).
   * 10.9L6 — "territórios" nunca passa do total da área: com +50% por volta a Floresta pediria 18
   * na Profundidade 2 tendo só 15 — virava missão impossível. Teto = explorar TODOS.
   */
  private targetOf(t: GoalTemplate, regionId: string): number {
    if (t.kind === "boss") return t.target;
    const d = Math.max(1, this.depth());
    const n = Math.max(1, Math.round(t.target * (1 + (d - 1) * GameConfig.depth.goalGrowth)));
    if (t.kind !== "territories") return n;
    const total = GameConfig.territories.list.filter((x) => x.regionId === regionId).length;
    return total > 0 ? Math.min(n, total) : n;
  }

  /** Objetivos da área, já com progresso e alvo resolvidos. */
  list(regionId: string): AreaGoal[] {
    const ts = this.templates(regionId);
    const p = this.progress[regionId] ?? [];
    return ts.map((t, i) => ({ kind: t.kind, progress: Math.max(0, p[i] ?? 0), target: this.targetOf(t, regionId) }));
  }

  /** 10.9L6 — o Caçador entrou no território: true só na 1ª vez nesta volta (aí a missão conta). */
  visitTerritory(regionId: string, territoryId: string): boolean {
    const s = (this.seen[regionId] ??= new Set());
    if (s.has(territoryId)) return false;
    s.add(territoryId);
    return true;
  }
  /** 10.9L6 — já visitou este território nesta volta? (o foco do Mapa acende os que faltam) */
  hasVisited(regionId: string, territoryId: string): boolean {
    return this.seen[regionId]?.has(territoryId) ?? false;
  }
  /** 10.9L6 — save de antes da correção (sem a lista de visitados): o Game recalcula a missão. */
  get needsTerritoryRecount(): boolean {
    return this.legacySave;
  }
  /**
   * 10.9L6 — RECÁLCULO (decisão do Thiago): a missão passa a valer o número REAL de territórios
   * visitados. `recount=false` só grava a lista (área já fechada nesta volta: não perde nada).
   */
  seedTerritories(regionId: string, ids: string[], recount: boolean): void {
    this.seen[regionId] = new Set(ids);
    if (!recount) return;
    const ts = this.templates(regionId);
    const i = ts.findIndex((t) => t.kind === "territories");
    if (i < 0) return;
    const p = (this.progress[regionId] ??= new Array(ts.length).fill(0));
    p[i] = Math.min(this.targetOf(ts[i], regionId), ids.length);
  }
  finishTerritoryRecount(): void {
    this.legacySave = false;
  }

  /** Todos os objetivos da área estão cumpridos? (é isto que abre o portão) */
  isComplete(regionId: string): boolean {
    const l = this.list(regionId);
    return l.length > 0 && l.every((g) => g.progress >= g.target);
  }

  /** Quantos faltam (para o HUD dizer o que ainda falta sem abrir tela nenhuma). */
  remaining(regionId: string): AreaGoal[] {
    return this.list(regionId).filter((g) => g.progress < g.target);
  }

  timesCleared(regionId: string): number {
    return this.cleared[regionId] ?? 0;
  }

  /** Conta um evento na área atual. Devolve true se ACABOU de completar a área. */
  advance(regionId: string, kind: AreaGoalKind, amount = 1): boolean {
    const ts = this.templates(regionId);
    if (ts.length === 0) return false;
    const was = this.isComplete(regionId);
    const p = (this.progress[regionId] ??= new Array(ts.length).fill(0));
    let touched = false;
    for (let i = 0; i < ts.length; i++) {
      if (ts[i].kind !== kind) continue;
      const target = this.targetOf(ts[i], regionId);
      if (p[i] >= target) continue;
      p[i] = Math.min(target, p[i] + amount);
      touched = true;
    }
    if (!touched) return false;
    const now = this.isComplete(regionId);
    if (now && !was) this.cleared[regionId] = this.timesCleared(regionId) + 1;
    return now && !was;
  }

  /** Zera o progresso de TODAS as áreas (nova volta de Profundidade). */
  resetForNewDepth(): void {
    for (const k of Object.keys(this.progress)) delete this.progress[k];
    for (const k of Object.keys(this.seen)) delete this.seen[k]; // 10.9L6 — nova volta: explorar de novo
  }

  snapshot(): AreaGoalsSnapshot {
    const byRegion: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(this.progress)) byRegion[k] = [...v];
    const seen: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(this.seen)) seen[k] = [...v];
    return { byRegion, cleared: { ...this.cleared }, seen };
  }

  load(data: AreaGoalsSnapshot | undefined): void {
    for (const k of Object.keys(this.progress)) delete this.progress[k];
    for (const k of Object.keys(this.cleared)) delete this.cleared[k];
    for (const k of Object.keys(this.seen)) delete this.seen[k];
    // 10.9L6 — save com progresso mas sem `seen` = de antes da correção (conta inflada) → recalcular
    this.legacySave = !!data && !data.seen && Object.keys(data.byRegion ?? {}).length > 0;
    for (const [rid, ids] of Object.entries(data?.seen ?? {})) {
      if (Array.isArray(ids)) this.seen[rid] = new Set(ids.filter((x): x is string => typeof x === "string"));
    }
    for (const [rid, arr] of Object.entries(data?.byRegion ?? {})) {
      if (!Array.isArray(arr)) continue;
      this.progress[rid] = arr.map((n) => Math.max(0, Math.floor(Number(n) || 0)));
    }
    for (const [rid, n] of Object.entries(data?.cleared ?? {})) {
      this.cleared[rid] = Math.max(0, Math.floor(Number(n) || 0));
    }
  }

  reset(): void {
    this.load(undefined);
  }
}
